import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  Stack,
  Button,
  Checkbox,
  Link,
  useColorMode,
  useColorStyle,
  Space,
  Grid,
  Divider,
  ModalHeader,
  Flex,
  Box,
} from '@tonic-one/react';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLoginUserStore } from '../../../models/useLoginUserModel';
import { BannerType } from '../../../utils/enums';
import {
  formatDeadlineDate,
  getDeadlineDateByPhase,
  gotoConnectToV1,
  gotoV1ForEveryone,
  isDeadlinePassed,
  PromotionPopupWindowPhaseEnum,
  updateUpgradeBannerClosedTime,
} from './shared';

type PopupType = BannerType | 'v1ForEveryone';
interface Props {
  bannerType: PopupType;
  phase: PromotionPopupWindowPhaseEnum;
}

function PhaseOneUpgradeModal({ bannerType, phase }: Props) {
  const { t, i18n } = useTranslation('header', {
    keyPrefix: 'header.banner.modal',
  });

  // Calculate formatted deadline date based on phase and locale
  const deadline = useMemo(() => {
    const dateStr = getDeadlineDateByPhase(phase);
    return formatDeadlineDate(dateStr, i18n.language);
  }, [phase, i18n.language]);

  // Check if deadline has passed - if so, hide the close option
  const deadlinePassed = useMemo(() => {
    if (import.meta.env.DEV) {
      const override = localStorage.getItem('dev_deadlinePassed');
      if (override === 'true') return true;
      if (override === 'false') return false;
    }
    return isDeadlinePassed(phase);
  }, [phase]);

  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [isOpen, setOpen] = useState(true);
  const [checked, setChecked] = useState(false);
  const { isRootUser, user } = useLoginUserStore();

  function renderSupport() {
    return (
      <>
        <Text
          alignSelf="flex-start"
          fontWeight="semibold"
          color={colorStyle.color.primary}
          mb="8px"
        >
          {t('second.support.title')}
        </Text>
        <Grid
          templateColumns="10px 1fr"
          columnGap="6px"
          color={colorStyle.color.secondary}
        >
          <Text>•</Text>
          <Text>
            <Trans i18nKey="header.banner.modal.second.support.desc">
              placeholder
              <Link
                href={t('popUpWindow.businessSuccessPortal.link')}
                target="_blank"
              >
                placeholder
              </Link>
              placeholder
            </Trans>
          </Text>
        </Grid>
      </>
    );
  }

  const renderHowToUpdate = () => {
    if (!isRootUser) {
      return (
        <>
          <Box
            mb="16px"
            style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
          >
            <Text>
              {t('second.how.p4', {
                accountEmail: user?.accountEmail,
                accountName: user?.accountName,
              })}
            </Text>
          </Box>
          {renderSupport()}
        </>
      );
    }

    if (bannerType === BannerType.Flywheel || bannerType === 'v1ForEveryone') {
      return (
        <>
          <Grid
            templateColumns="10px 1fr"
            columnGap="6px"
            color={colorStyle.color.secondary}
          >
            <Text>1.</Text>
            <Text>
              <Trans i18nKey="header.banner.modal.second.how.p1">
                placeholder
                <Link
                  href={t('popUpWindow.howToUpdateV1InitialLicenseVideo.link')}
                  target="_blank"
                >
                  placeholder
                </Link>
                placeholder
              </Trans>
            </Text>
            <Text>2.</Text>
            <Text>
              <Trans i18nKey="header.banner.modal.second.how.p2">
                placeholder
                <Link
                  href={t('popUpWindow.howToUpdateV1ConnectProductVideo.link')}
                  target="_blank"
                >
                  placeholder
                </Link>
              </Trans>
            </Text>
            <Text>3.</Text>
            <Text>
              <Trans i18nKey="header.banner.modal.second.how.p3">
                placeholder
                <Link
                  href={t('popUpWindow.howToUpdateV1UpdateToProductVideo.link')}
                  target="_blank"
                >
                  placeholder
                </Link>
              </Trans>
            </Text>
          </Grid>
          <Space mt={16} />
          {renderSupport()}
        </>
      );
    }

    return (
      <>
        <Grid
          templateColumns="10px 1fr"
          columnGap="6px"
          color={colorStyle.color.secondary}
        >
          <Text>1.</Text>
          <Text>
            <Trans i18nKey="header.banner.modal.second.how.p2">
              placeholder
              <Link
                href={t('popUpWindow.howToUpdateV1ConnectProductVideo.link')}
                target="_blank"
              >
                placeholder
              </Link>
            </Trans>
          </Text>
          <Text>2.</Text>
          <Text>
            <Trans i18nKey="header.banner.modal.second.how.p3">
              placeholder
              <Link
                href={t('popUpWindow.howToUpdateV1UpdateToProductVideo.link')}
                target="_blank"
              >
                placeholder
              </Link>
            </Trans>
          </Text>
        </Grid>
        <Space mt={16} />
        {renderSupport()}
      </>
    );
  };

  const onUpdateClick = () => {
    // isn't a V1 customer, The update now button will follow our flywheel signup flow to guide customers to register V1 account
    if (bannerType === BannerType.Flywheel) {
      // gotoFlywheelSignup(notificationContent);
      gotoV1ForEveryone();
      return;
    }

    if (bannerType === 'v1ForEveryone') {
      gotoV1ForEveryone();
      return;
    }
    //is V1 customer：The update now button will redirect to V1ECS-CECP landing page to guide customers to do solution update
    gotoConnectToV1();
  };

  return (
    <Modal
      id="upgradePopup"
      isOpen={isOpen}
      size="md"
      /**
       * tonic zIndex link: https://trendmicro-frontend.github.io/tonic-ui/react/v2/theme/z-indices/
       * tonic modal default z-index is 1500, upgradePopup has higher priority
       */
      zIndex={1501}
      color={colorStyle.color.primary}
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent>
        <ModalHeader textAlign="center" m="8px 8px 0px 16px">
          <Text size="2xl">{t('second.title')}</Text>
        </ModalHeader>
        <ModalBody
          display="flex"
          flexDirection="column"
          alignItems="center"
          m="0px 8px 0px 8px"
        >
          <Text
            textAlign="center"
            fontSize="16px"
            fontWeight={400}
            lineHeight="22px"
            dangerouslySetInnerHTML={{
              __html: deadlinePassed
                ? t('second.subTitle.deadlinePassed')
                : t('second.subTitle', { deadline }),
            }}
          ></Text>

          <Text alignSelf="flex-start" mt="16px" mb="8px" fontWeight="semibold">
            {t('offers.title')}
          </Text>
          <Grid
            templateColumns="10px 1fr"
            columnGap="6px"
            color={colorStyle.color.secondary}
          >
            <Text>•</Text>
            <Text>
              <Space fontWeight="bold">{t('offers.prefix.p1')}</Space>{' '}
              {t('offers.suffix.p1')}
            </Text>
            <Text>•</Text>
            <Text>
              {' '}
              <Space fontWeight="bold">{t('offers.prefix.p2')}</Space>{' '}
              {t('offers.suffix.p2')}
            </Text>
            <Text>•</Text>
            <Text>
              {' '}
              <Space fontWeight="bold">{t('offers.prefix.p3')}</Space>{' '}
              {t('offers.suffix.p3')}
            </Text>
            <Text>•</Text>
            <Text>
              {' '}
              <Space fontWeight="bold">{t('offers.prefix.p4')}</Space>{' '}
              {t('offers.suffix.p4')}
            </Text>
          </Grid>
          {/* how update */}
          <Text alignSelf="flex-start" mt="16px" mb="8px" fontWeight="semibold">
            {t('how.title')}
          </Text>
          <Stack alignSelf="flex-start" color={colorStyle.color.secondary}>
            {renderHowToUpdate()}
          </Stack>

          {/*<Space height="36px" />*/}

          {/* update button */}
          {isRootUser && (
            <>
              <Button
                mt={16}
                py="12px"
                px="60px"
                size="lg"
                border="none"
                variant="primary"
                alignSelf="stretch"
                onClick={onUpdateClick}
              >
                {t('update')}
              </Button>
            </>
          )}

          {/* divider and close option - hidden after deadline */}
          {!deadlinePassed && (
            <>
              <Divider
                variant="dashed"
                orientation="horizontal"
                height="1px"
                width="100%"
                my="24px"
              />

              {/* close */}
              <Link
                onClick={() => {
                  setOpen(false);
                  updateUpgradeBannerClosedTime();
                }}
                disabled={!checked}
              >
                {t('notNow')}
              </Link>
              <Space height="22px" />
              <Flex alignItems="flex-start" columnGap="2x">
                <Box py="1h">
                  <Checkbox
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                  ></Checkbox>
                </Box>
                <Box
                  dangerouslySetInnerHTML={{
                    __html: !isRootUser
                      ? t('localAccount.confirm', { deadline })
                      : t('second.confirm', { deadline }),
                  }}
                />
              </Flex>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default PhaseOneUpgradeModal;
